import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Navbar, Nav } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { faUser, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    clearQuestionnaireMetaData,
    updateQuestionnaireConfig,
} from "src/redux/actions/questionnaireActions";
import { isValidLastPage } from "src/legacy/utils/helpers";
import {
    clearModalContent,
    setModalContent,
} from "src/redux/modules/modalEvent";
import { PauseOnboardingModal } from "src/containers/TherapistClientProfile";
import { cancelOnboarding } from "src/api/user";
import { faCirclePause } from "@fortawesome/free-regular-svg-icons";
import { Typography } from "src/latest/src/components";
import { OnboardingActionStatus } from "src/containers/TherapistClientProfile/Tabs/onboarding/modals/PausedOnboardingModalConstant";
import { UserTypes } from "../../constants";
import { setFlashNotification } from "src/redux/modules/flashNotification";
import { useTranslation } from "react-i18next";

type Props = {
    currentUser: any;
    handleNav: (path: string) => void;
};

export const OnboardingHeaderContent = ({ currentUser, handleNav }: Props) => {
    const dispatch = useDispatch();
    const {t} = useTranslation('components');
    const { lastPageEdited } = useSelector(({ userIntake }: any) => userIntake);
    const { isAuthed, loggedInUser } = useSelector(
        (state: any) => state?.authentication
    );
    const { userRole, id } = loggedInUser;
    const headerContent = useSelector(
        (state: any) => state?.questionnaireData?.currentConfig?.header.content
    );
    const userId = useSelector(
        (state: any) => state?.questionnaireData?.initialMetadata?.user_id
    );
    const isPreonboardedUser = useSelector(
        (state: any) =>
            state?.questionnaireData?.initialMetadata?.is_preonboarded_user
    );
    const questionnaireType = useSelector(
        (state: any) =>
            state?.questionnaireData?.initialMetadata?.questionnaire_group?.type
    );
    const needHelpSelfMatchContactNumber = useSelector(
        (state: any) => state?.configuration?.needHelpSelfMatchContactNumber
    );

    const userType = useSelector(
        (state: any) => state?.onboarding?.userToBeOnboarded?.user_type
    );
    const activeLinkRef = useRef<HTMLAnchorElement>(null);
    const activeIndex = headerContent ? headerContent.findIndex((item: any) => item.isSelected) : -1;
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const isFewSteps = headerContent && headerContent.length < 3;

    const checkScroll = useCallback(() => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(Math.ceil(scrollLeft) < scrollWidth - clientWidth - 2);
        }
    }, []);

    useEffect(() => {
        checkScroll();
        let animationTimer: number;

        if (activeLinkRef.current) {
            requestAnimationFrame(() => {
                activeLinkRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "center"
                });
                animationTimer = window.setTimeout(checkScroll, 350);
            });
        }

        window.addEventListener("resize", checkScroll);

        return () => {
            window.removeEventListener("resize", checkScroll);

            if (animationTimer) {
                clearTimeout(animationTimer);
            }
        };
    }, [headerContent, checkScroll]);

    const setSelectCategoryHeader = (categoryId: string) => {
        dispatch(updateQuestionnaireConfig(categoryId));
    };

    const openCancelOnboardingModal = () => {
        dispatch(
            setModalContent({
                modalContent: (
                    <PauseOnboardingModal
                        handleOnboardingStatus={onCancelOnboarding}
                        onboardingActionStatus={OnboardingActionStatus.CANCEL_ONBOARDING}
                        userType={userType?.includes(UserTypes?.CLIENT) ? UserTypes?.CLIENT : ""}
                    />
                ),
                title: (
                    <Typography color="color-28" variant="size-20">
                        {t('headerComponent.onboardingHeaderContent.cancelOnboardingModal.title')}
                    </Typography>
                ),
                size: "sm",
                isGeneralPopup: true,
                backDrop: "static",
                className: `${userType?.includes(UserTypes?.CLIENT) ? 'cancel-onboarding-client' : 'cancel-onboarding'}`
            })
        );
    };

    const onCancelOnboarding = async (note?: string, pausedReason?: string | null) => {
        await cancelOnboarding(userId, note, pausedReason);
        dispatch(clearQuestionnaireMetaData());
        dispatch(clearModalContent());
        dispatch(setFlashNotification({
            message: t('headerComponent.onboardingHeaderContent.cancelOnboardingModal.notificationMessage')
        }));

        if (
            questionnaireType &&
            questionnaireType === "SELF_MATCHING_CLIENT_INTAKE"
        ) {
            handleNav("/therapy/clinician-availability");
        } else {
            handleNav("/therapy/scheduler");
        }
    };

    return (
        <Fragment>
           <div id="questionnaire-header" className={`order-3 d-flex   ${isFewSteps ? 'few-steps-mobile' : ''}`}>
                {showLeftArrow && (
                    <div className="mobile-arrow left-arrow d-lg-none" >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </div>
                )}

                <Nav
                    ref={scrollContainerRef}
                    onScroll={checkScroll}
                    className={`m${userRole?.toLowerCase() === "client" &&
                            isValidLastPage(lastPageEdited)
                            ? "x"
                            : "x"
                        }-auto`}
                >
                    {headerContent &&
                        headerContent.map((item: any, i: number) => {
                            const isPassed = i < activeIndex;
                            const isActive = item.isSelected;

                            return (
                                <Nav.Link
                                    key={i}
                                    ref={item.isSelected ? activeLinkRef : null}
                                    className={`${!id ? "d-none" : "d-block"} ${item.isSelected ? "active" : ""
                                        }`}
                                    onClick={() => {
                                        setSelectCategoryHeader(item.id);
                                    }}
                                >
                                    <div>
                                        <Typography
                                            color={isActive ? "color-2" : "color-29"}
                                            variant="size-12"
                                            weight={isActive || isPassed ? "bold" : "semi-bold"}
                                            className={`text-uppercase ${isPassed ? 'passed-text' : ''}`}
                                        >
                                            {item.name}
                                        </Typography>
                                            <span className={isPassed ? 'passed-dash' : ''} />
                                    </div>
                                </Nav.Link>
                            );
                        })}
                </Nav>
                {showRightArrow && (
                    <div className="mobile-arrow right-arrow d-lg-none">
                        <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                )}
            </div>
            <Navbar.Toggle aria-controls="onboarding-menu">
                {[...Array(3)].map((_, i) => (
                    <span key={i} className="menu-stripped" />
                ))}
            </Navbar.Toggle>
            <Navbar.Collapse id="onboarding-menu" className="order-4 mobile-floating-card">
                <div className="username-section">
                    {userId && id !== userId && currentUser ? (
                        (!currentUser?.full_name || !currentUser?.full_name?.trim()) ? (
                            <div>
                                <Typography
                                    color="color-35"
                                    weight="normal"
                                    variant="size-12"
                                    className="me-2 mb-1"
                                >
                                    <FontAwesomeIcon icon={faUser} /> {t('headerComponent.onboardingHeaderContent.profileCreationMessage')}{" "}
                                </Typography>
                                <Typography
                                    color="color-2"
                                    weight="bold"
                                    variant="size-12"
                                >
                                    {currentUser?.email}
                                </Typography>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <Typography
                                        color="color-35"
                                        weight="normal"
                                        variant="size-12"
                                        className="me-2 mb-1"
                                    >
                                        <FontAwesomeIcon icon={faUser} /> {t('headerComponent.onboardingHeaderContent.profileCreationMessage')}{" "}
                                    </Typography>
                                </div>
                                <div>
                                    <Typography
                                        color="color-2"
                                        weight="bold"
                                        variant="size-12"
                                    >
                                        {currentUser?.full_name}
                                    </Typography>
                                </div>
                            </>
                        )
                    ) : (
                        ""
                    )}
                </div>

                {headerContent && (
                    <div className="intake-logout">
                        <Nav>
                            {questionnaireType &&
                                questionnaireType ===
                                "SELF_MATCHING_CLIENT_INTAKE" &&
                                !isAuthed && needHelpSelfMatchContactNumber && (
                                    <>
                                        <Typography
                                            color="color-28"
                                            weight="regular"
                                            variant="size-12"
                                            className="d-inline-block text-right"
                                        >
                                            {t('headerComponent.onboardingHeaderContent.supportContactText')}
                                            <br />
                                            <Typography
                                                color="color-27"
                                                weight="extra-bold"
                                                variant="size-12"
                                                className="d-inline-block mt-2"
                                                component="span"
                                            >
                                                {needHelpSelfMatchContactNumber}
                                            </Typography>
                                        </Typography>
                                    </>
                                )}
                        </Nav>
                        <Nav>
                            {/* // TODO: show this when user is not client */}
                            {isAuthed && isPreonboardedUser && (
                                <>
                                    <Nav.Link
                                        onClick={() =>
                                            openCancelOnboardingModal()
                                        }
                                    >
                                        <Typography
                                            color="color-29"
                                            variant="size-12"
                                            weight="bold"
                                            className="text-uppercase intake-logout-text"
                                        >
                                            <FontAwesomeIcon
                                                className="me-2"
                                                icon={faCirclePause}
                                            />
                                            {t('headerComponent.onboardingHeaderContent.buttonText.cancelOnboarding')}
                                        </Typography>
                                    </Nav.Link>
                                </>
                            )}
                        </Nav>
                        <Nav>
                            {id && (
                                <Nav.Link onClick={() => handleNav("/logout")}>
                                    <Typography
                                        color="color-29"
                                        variant="size-12"
                                        weight="bold"
                                        className="text-uppercase intake-logout-text"
                                    >
                                        {t('headerComponent.onboardingHeaderContent.buttonText.logOut')}
                                    </Typography>
                                </Nav.Link>
                            )}
                            {!id && (
                                <Nav.Link
                                    onClick={() => handleNav("/login")}
                                    className="text-center"
                                >
                                    <Typography
                                        color="color-27"
                                        variant="size-12"
                                        weight="bold"
                                        className="text-uppercase intake-logout-text"
                                    >
                                        {t('headerComponent.onboardingHeaderContent.buttonText.logIn')}
                                    </Typography>
                                </Nav.Link>
                            )}
                        </Nav>
                    </div>
                )}
            </Navbar.Collapse>
        </Fragment>
    );
};
